/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */

interface IAssignType {
    <T, U>(target: T, src: U): T & U;
    <T, U, V>(target: T, src1: U, src2: V): T & U & V;
    <T, U, V, W>(target: T, src1: U, src2: V, src3: W): T & U & V & W;
    (target: object, ...sources: any[]): any;
}

/**
 * Copy the values of all of the enumerable own properties from one or more source objects to a
 * target object. Returns the target object.
 */
const assign: IAssignType =
    (Object as any).assign ||
    ((target: object, ...sources: any[]) => {
        if (target === null || typeof target === 'undefined') {
            throw new TypeError('Cannot assign without target object');
        }
        const dst = Object(target) as Record<string, any>;

        for (const src of sources) {
            if (typeof src === 'object') {
                for (const key in src) {
                    if (src.hasOwnProperty(key)) {
                        dst[key] = src[key];
                    }
                }
            }
        }
    });

/**
 * Returns the names of the enumerable properties and methods of an object.
 * @param o Object that contains the properties and methods.
 */
const keys = Object.keys;

interface IValuesType {
    <T>(o: { [key: string]: T } | ArrayLike<T>): T[];
    (o: {}): any[];
}
/**
 * Returns an array of values of the enumerable properties of an object
 * @param o Object that contains the properties and methods.
 */
const values: IValuesType = (Object as any).values || ((o: any) => keys(o).map((key) => o[key]));

interface IEntriesType {
    <T>(o: { [key: string]: T } | ArrayLike<T>): Array<[string, T]>;
    (o: {}): Array<[string, any]>;
}
/**
 * Returns an array of key/values of the enumerable properties of an object
 * @param o Object that contains the properties and methods.
 */
const entries: IEntriesType = (Object as any).entries || ((o: any) => keys(o).map((key) => [key, o[key]]));

/**
 * ObjectShim provides functionality of ES2017 Object for ES5 browsers.
 */
const ObjectShim = {
    ...Object,
    assign,
    entries,
    keys,
    values,
};

export default ObjectShim;
